/* Hallmark · component: Button · genre: modern-minimal · theme: maeiz-tokens
 * states: default · hover · focus · active · disabled · loading
 * motion: none — forwardRef required for Radix asChild
 */
import React from "react";
import { clsx } from "clsx";
import type { LucideIcon } from "lucide-react";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | "primary"
        | "secondary"
        | "tertiary"
        | "outline"
        | "outline-secondary"
        | "ghost"
        | "gold"
        | "outline-gold"
        | "danger"
        | "unknown";
    size?: "sm" | "md" | "lg";
    isLoading?: boolean;
    fullWidth?: boolean;
    icon?: LucideIcon | React.ReactNode;
    iconPosition?: "start" | "end";
    leftIcon?: LucideIcon | React.ReactNode;
    rightIcon?: LucideIcon | React.ReactNode;
}

const iconPx = { sm: 16, md: 18, lg: 20 } as const;

function renderIcon(icon: LucideIcon | React.ReactNode | undefined, size: "sm" | "md" | "lg"): React.ReactNode {
    if (icon == null || icon === false) return null;
    if (React.isValidElement(icon)) return icon;
    if (typeof icon === "function" || typeof icon === "object") {
        const Comp = icon as LucideIcon;
        return <Comp size={iconPx[size]} aria-hidden className="shrink-0" strokeWidth={2} />;
    }
    return null;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            variant = "primary",
            size = "md",
            isLoading = false,
            fullWidth = false,
            icon,
            iconPosition = "start",
            leftIcon,
            rightIcon,
            disabled,
            className,
            type = "button",
            ...props
        },
        ref,
    ) => {
        const baseStyles = clsx(
            "inline-flex items-center justify-center gap-2 font-semibold rounded-xl",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:saturate-50",
            "select-none",
        );

        const variantStyles: Record<NonNullable<ButtonProps["variant"]>, string> = {
            primary:
                "bg-primary text-white hover:bg-primary-dark active:bg-primary-dark focus-visible:ring-primary shadow-mz-sm",
            secondary:
                "bg-secondary text-white hover:bg-secondary-dark active:bg-secondary-dark focus-visible:ring-secondary shadow-mz-sm",
            tertiary:
                "bg-warning text-white hover:bg-warning-dark active:bg-warning-dark focus-visible:ring-warning shadow-mz-sm",
            outline:
                "border border-primary/80 bg-transparent text-primary hover:bg-primary/5 active:bg-primary/10 focus-visible:ring-primary dark:text-gold-light dark:border-gold/60 dark:hover:bg-gold/10",
            "outline-secondary":
                "border border-secondary/80 bg-transparent text-secondary hover:bg-secondary/5 active:bg-secondary/10 focus-visible:ring-secondary",
            ghost:
                "bg-transparent text-neutral-dark hover:bg-neutral-dark/5 active:bg-neutral-dark/10 focus-visible:ring-primary dark:text-neutral-light dark:hover:bg-white/10",
            gold: "bg-gold text-white hover:bg-gold-dark active:bg-gold-dark focus-visible:ring-gold shadow-mz-sm",
            "outline-gold":
                "border border-gold bg-transparent text-gold hover:bg-gold/10 active:bg-gold/15 focus-visible:ring-gold",
            danger:
                "bg-danger text-white hover:bg-danger-dark active:bg-danger-dark focus-visible:ring-danger shadow-mz-sm",
            unknown: "",
        };

        const sizeStyles = {
            sm: "px-3 h-9 text-sm",
            md: "px-4 h-11 text-sm",
            lg: "px-5 h-[52px] text-base",
        };

        const iconNode = !isLoading ? renderIcon(icon, size) : null;
        const leftIconNode = !isLoading && leftIcon ? renderIcon(leftIcon, size) : null;
        const rightIconNode = !isLoading && rightIcon ? renderIcon(rightIcon, size) : null;

        return (
            <button
                ref={ref}
                type={type}
                className={clsx(baseStyles, variantStyles[variant], sizeStyles[size], fullWidth && "w-full", className)}
                disabled={disabled || isLoading}
                data-state={isLoading ? "loading" : undefined}
                {...props}
            >
                {isLoading ? (
                    <>
                        <svg className="h-4 w-4 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                        <span>{typeof children === "string" ? "Loading…" : children}</span>
                    </>
                ) : (
                    <>
                        {leftIconNode ?? (iconPosition === "start" ? iconNode : null)}
                        {children}
                        {rightIconNode ?? (iconPosition === "end" ? iconNode : null)}
                    </>
                )}
            </button>
        );
    },
);

Button.displayName = "Button";
